import { useState } from "react";
import {
    TextField,
    Button,
    Box,
    Grid,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from "@mui/material";
import { updateSpace } from "../../../service/spaceService";
import { Space } from "../../../types/spaceTypes";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

interface EditSpaceProps {
    open: boolean;
    onClose: () => void;
    space: Space | null;
    refreshSpaces: () => void;
}

const EditSpace: React.FC<EditSpaceProps> = ({ open, onClose, space, refreshSpaces }) => {
    const [loading, setLoading] = useState<boolean>(false);
    
    const validationSchema = Yup.object({
        owner_id: Yup.string().required("Owner is required"),
    });

    const formik = useFormik({
        initialValues: {
            space_name: space?.space_name || "",
            space_type: space?.space_type || "",
            owner_id: space?.owner_id || "",
        },
        enableReinitialize: true, // ✅ Update form values when space changes
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                await updateSpace(space?.id as number, values);
                toast.success("Space updated successfully!");
                refreshSpaces();
                onClose();
            } catch (error) {
                toast.error("Failed to update space.");
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Space</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%", p: 2 }}>
                    <Grid container spacing={2}>
                        {/* First Row - Space Name */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Space Name"
                                name="space_name"
                                value={formik.values.space_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.space_name && Boolean(formik.errors.space_name)}
                                helperText={formik.touched.space_name && formik.errors.space_name}
                                required />
                        </Grid>
                    </Grid>

                    <DialogActions sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={2}>
                            <Button variant="contained" color="primary" type="submit" disabled={loading}>
                                {loading ? <CircularProgress size={24} /> : "Save Changes"}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </Stack>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default EditSpace;
